
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, ChevronDown, ChevronUp, Clock, User, Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function MobileTransactionCard({
  transaction,
  getStatusColor,
  getTypeColor,
  getTypeLabel,
  formatCurrency,
  handleEdit,
  handleDelete
}) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };
  
  // Function to format user info nicely - updated to prefer email display
  const formatUserInfo = (userId) => {
    if (!userId) return "—";
    
    // If it looks like an email, return it directly
    if (userId.includes('@')) {
      return userId;
    }
    
    // Otherwise show "Usuário" instead of the UUID
    return "Usuário";
  };

  // Get customer name or transaction name based on type
  const getDisplayName = () => {
    // Fix: ensure consistent display of client name across transaction types
    if (transaction.type === "entrada" || transaction.type === "saida") {
      return transaction.customers?.name || 
             (transaction.type === "entrada" ? "Cliente não especificado" : transaction.name || "—");
    } else {
      return transaction.name || transaction.category || "—";
    }
  };

  return (
    <div className="bg-[#1A1E2C] dark:bg-[#1A1E2C] rounded-lg shadow mb-3 overflow-hidden border border-gray-800">
      {/* Header */}
      <div className="flex justify-between items-center p-3 bg-[#252A38] dark:bg-[#252A38]">
        <div className={`font-medium ${getTypeColor(transaction.type)}`}>
          {getTypeLabel(transaction.type)}
        </div>
        <div className={`font-bold ${getTypeColor(transaction.type)}`}>
          {formatCurrency(transaction.amount)}
        </div>
      </div>

      {/* Main content */}
      <div className="p-3">
        {/* Nome do cliente/despesa com destaque maior */}
        <div className="text-xl font-bold mb-3 text-white dark:text-white">
          {getDisplayName()}
        </div>

        {/* Show customer info for 'saida' transactions if available */}
        {transaction.type === "saida" && transaction.customers?.name && (
          <div className="mt-1 mb-3 text-sm text-white/80">
            <span className="font-medium">Cliente:</span> {transaction.customers.name}
          </div>
        )}

        {/* Status de pagamento e data */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-300 dark:text-gray-300 flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {formatDate(transaction.due_date)}
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(
              transaction.payment_status
            )}`}
          >
            {transaction.payment_status}
          </span>
        </div>
        
        {/* Description sempre visível com destaque maior */}
        {transaction.description && (
          <div className="mt-2 mb-3 text-sm text-white dark:text-white bg-[#252A38] dark:bg-[#252A38] p-3 rounded">
            <span className="font-semibold block mb-1 text-white dark:text-white">Descrição:</span> 
            {transaction.description}
          </div>
        )}

        {/* Informações expandidas - apenas quando clicado em "Mais" */}
        {expanded && (
          <div className="text-sm text-gray-300 dark:text-gray-300 mt-2 flex flex-col space-y-2 bg-[#252A38] dark:bg-[#252A38] p-3 rounded">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2 text-[#0073B7]" />
              <span className="font-medium">Registrado por:</span>
              <span className="ml-2">{formatUserInfo(transaction.created_by)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-[#0073B7]" />
              <span className="font-medium">Data e hora:</span>
              <span className="ml-2">{formatDateTime(transaction.created_at)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex border-t border-gray-800 dark:border-gray-800">
        <Button
          variant="ghost"
          className="flex-1 rounded-none py-2 text-sm text-gray-300 dark:text-gray-300 hover:bg-[#252A38] hover:dark:bg-[#252A38]"
          onClick={toggleExpand}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" /> Menos
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" /> Mais
            </>
          )}
        </Button>
        <div className="w-px bg-gray-800 dark:bg-gray-800" />
        <Button
          variant="ghost"
          className="flex-1 rounded-none py-2 text-sm text-[#0073B7] hover:bg-[#252A38] hover:dark:bg-[#252A38]"
          onClick={() => handleEdit(transaction)}
        >
          <Edit className="h-4 w-4 mr-1" /> Editar
        </Button>
        <div className="w-px bg-gray-800 dark:bg-gray-800" />
        <Button
          variant="ghost"
          className="flex-1 rounded-none py-2 text-sm text-[#E60026] hover:bg-[#252A38] hover:dark:bg-[#252A38]"
          onClick={() => handleDelete(transaction)}
        >
          <Trash2 className="h-4 w-4 mr-1" /> Excluir
        </Button>
      </div>
    </div>
  );
}
